<script lang="ts">
  import { browser } from '$app/environment'
  import { PUBLIC_GOOGLE_ANALYTICS_ID } from '$env/static/public'
  import { loadMyTeams } from '$lib/components/component-functions'
  import HandlerContactCompanies from '$lib/components/handlers/HandlerContactCompanies.svelte'
  import HandlerNotifications from '$lib/components/handlers/HandlerNotifications.svelte'
  import HandlerTags from '$lib/components/handlers/HandlerTags.svelte'
  import HandlerUsage from '$lib/components/handlers/HandlerUsage.svelte'
  import type { SavedDeviceIds } from '$lib/components/models'
  import MolComingSoonModal from '$lib/components/mols/MolComingSoonModal.svelte'
  import { createLocalStorage, disableWarnings, persist } from '$lib/svelte-persistent-store-main/src/index'
  import { GoogleAnalytics } from '@beyonk/svelte-google-analytics'
  import routes from './routes'
  import { goto } from '$app/navigation'
  import '@fontsource/inter/400.css'
  import '@fontsource/inter/600.css'
  import '@fontsource/inter/700.css'
  import '@fontsource/lato/400.css'
  import '@fontsource/lato/700.css'
  import dayjs from 'dayjs'
  import 'dayjs/locale/en'
  import 'dayjs/locale/fr'
  import type { Subscription, Team, TeamUser, User } from 'fixee-server'
  import type { BrowserPermissions } from 'fixee-server/lib/services/portal/portal.shared'
  import { onDestroy, onMount, setContext } from 'svelte'
  import { Toaster } from 'svelte-french-toast'
  import { _, locale } from 'svelte-i18n'
  import { get, writable } from 'svelte/store'
  import { fade } from 'svelte/transition'
  import { pwaInfo } from 'virtual:pwa-info'
  import { initFirebase, requestNotificationPermission, subscribeSwToPushNotifications } from '../api/firebase'
  import useFixeeClient from '../api/fixee-client'
  import { inDevelopment, inProduction, inStaging } from '../env'
  import '../i18n'
  import { fallbackLocale } from '../i18n'
  import {
    SELECTED_LOCALE,
    SIDEBAR,
    UNIQUE_DEVICE_ID,
    currentBrowserSupportsPush,
    iOS,
    isClientSide,
    randomUUID,
  } from '../utils'
  import {
    getApiStatusContext,
    getBrowserPermissionsContext,
    getChatClientContext,
    getCurrentTeamUserContext,
    getTeamsContext,
    getIsIssuerViewContext,
    getWindowSizeContext,
    type ApiStatus,
    type GlobalVars,
    type RGPDAccept as RGPDAcceptType,
    type WindowSize,
  } from './contexts'
  // import { registerSW } from 'virtual:pwa-register';
  import { page } from '$app/stores'
  import MolVideoPermissionsWarning from '$lib/components/mols/MolVideoPermissionsWarning.svelte'
  import MolFrontendVersionUpdate from '$lib/components/mols/MolFrontendVersionUpdate.svelte'
  import { initConsoleLogger } from '$lib/utils/console-logger'
  import { checkVersion } from '$lib/utils/version-check'
  import * as Sentry from '@sentry/svelte'
  import { Client as ConversationsClient } from '@twilio/conversations'
  import 'flag-icons/css/flag-icons.min.css'
  import './styles.css'
  import { reportsStore } from '../stores/reportsStore'

  // https://vite-pwa-org.netlify.app/guide/auto-update.html#automatic-reload
  // This causes a build error in staging for now ?? https://gitlab.com/fixly/fixly-web/-/jobs/5048319515
  // registerSW({ immediate: true });

  // The warnings are supposed to be hidden, they are not really for some reason : https://github.com/MacFJA/svelte-persistent-store/issues/9
  disableWarnings()

  if (browser) {
    initConsoleLogger()
  }

  // Handle dark theme
  const theme = persist(writable<'dark' | 'light'>('light'), createLocalStorage(), 'theme')
  setContext('theme', theme)

  let loading = true
  $: if (browser) {
    setTimeout(() => {
      loading = false
    }, 300)
  }

  $: {
    if (browser) {
      if ($theme === 'dark') {
        // Disable dark theme for now ?
        console.log('Dark theme is disabled for now')
        // document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark')
      }
    }
  }

  // Handle CGU consent, should cookie banner if not accepted
  const RGPDAccept = persist(writable<RGPDAcceptType>({}), createLocalStorage(), 'RGPDAccept')
  setContext('RGPDAccept', RGPDAccept)

  // Update the global dayjs locale
  $: dayjs.locale($locale || fallbackLocale)

  // Handle permissions
  const defaultPermissions: BrowserPermissions = {
    camera: 'unknown',
    microphone: 'unknown',
    notifications: 'unknown',
    geolocation: 'unknown',
  }

  const isValidPermissions = (value: any): value is BrowserPermissions => {
    return (
      value &&
      typeof value === 'object' &&
      typeof value.camera === 'string' &&
      typeof value.microphone === 'string' &&
      typeof value.notifications === 'string' &&
      typeof value.geolocation === 'string'
    )
  }

  const permissionsStore = persist(
    writable<BrowserPermissions>({ ...defaultPermissions }),
    createLocalStorage(),
    'permissions',
  )

  const currentValue = get(permissionsStore)
  if (!isValidPermissions(currentValue)) {
    permissionsStore.set({ ...defaultPermissions })
  }

  setContext('permissions', permissionsStore)
  const permissions = getBrowserPermissionsContext()

  const handleMyPermissions = async () => {
    const names = ['microphone', 'notifications', 'camera', 'geolocation']

    for (const name of names) {
      try {
        const perm = await navigator.permissions.query({
          name: name as any,
        })

        $permissions[name as keyof BrowserPermissions] = perm.state
        perm.onchange = event => {
          const state = (event.target as any)?.state
          if (state) {
            $permissions[name as keyof BrowserPermissions] = perm.state
            console.log('Permission changed', name, perm.state)

            if (name === 'notifications' && perm.state === 'granted') {
              // We "request" the permission, this is actually just to trigger the creation of the subscription and save it in the database
              requestNotificationPermission($_)
            }
          }
        }
      } catch (error) {
        console.log('Browser does not support permissions API for', name)
        // For firefox for example, which does not suport "camera" and "microphone" permission query, we set it to prompt always.
        // Otherwise there is a risk that the user denies the permission, we save it in the localstorage and don't ask again because of that even if the didn't check "remember this choice".
        // In this case the user can be prompted again after a refresh
        $permissions[name as keyof BrowserPermissions] = 'prompt'
      }
    }
  }

  // Handle current user and other contexts
  const currentUser = persist(writable<User | undefined>(undefined), createLocalStorage(), 'currentUser')
  setContext('currentUser', currentUser)
  setContext('currentTeamUser', writable<TeamUser | undefined>(undefined))
  const currentTeamUser = getCurrentTeamUserContext()
  const currentTeam = persist(writable<Team | undefined>(undefined), createLocalStorage(), 'currentTeam')
  setContext('currentTeam', currentTeam)
  const currentSubscription = persist(
    writable<Subscription | undefined>(undefined),
    createLocalStorage(),
    'currentSubscription',
  )
  setContext('currentSubscription', currentSubscription)
  setContext('chatClient', writable(undefined))
  setContext('teams', writable([]))
  setContext('isIssuerView', writable(true))
  setContext('apiStatus', writable<ApiStatus>('disconnected'))
  setContext(
    'deviceIds',
    persist(writable<SavedDeviceIds>({ audioEnabled: true, videoEnabled: true }), createLocalStorage(), 'deviceIds'),
  )
  // A general context for leftover variables
  setContext('globalVars', writable<GlobalVars>({}))
  setContext(
    'windowSize',
    writable<WindowSize>({
      height: 0,
      width: 0,
    }),
  )

  const teams = getTeamsContext()
  const apiStatus = getApiStatusContext()
  const windowSize = getWindowSizeContext()
  const chatClient = getChatClientContext()
  const isIssuerView = getIsIssuerViewContext()

  const sidebarStore = writable<{ isOpen: boolean; isCollapsed: boolean }>({
    isOpen: true,
    isCollapsed: $windowSize.width < 1024,
  })
  setContext(SIDEBAR, sidebarStore)

  const setWindowValues = () => {
    if (!isClientSide()) return
    // Mobile: fully close sidebar
    if (window.innerWidth < 768) {
      sidebarStore.set({
        isOpen: false,
        isCollapsed: false,
      })
      // Tablet: collapse sidebar; open only if resized wider
    } else if (!$sidebarStore.isOpen && window.innerWidth < 1024) {
      $sidebarStore.isCollapsed = true
      $sidebarStore.isOpen = window.innerWidth >= $windowSize.width
      // Desktop: expand sidebar; open only if resized wider
    } else if (window.innerWidth >= 1024) {
      $sidebarStore.isCollapsed = false
      $sidebarStore.isOpen = window.innerWidth >= $windowSize.width
    }
    $windowSize = {
      width: window.innerWidth,
      height: window.innerHeight,
    }
  }

  $: onCurrentTeamChanged($currentTeam)

  const onCurrentTeamChanged = (team?: Team) => {
    $currentSubscription = team?.subscription
  }

  const onTeamPatched = (team: Team) => {
    if ($currentTeam?.id === team.id) {
      $currentTeam = team
      if (!$currentTeam?.subscription) {
        $currentTeam.subscription = $currentSubscription
      }
    }
    $teams = $teams.map(t => (t.id === team.id ? team : t))
  }

  const loadPwaModal = async () => {
    if (browser) {
      // Disable it for now, we use autoUpdate instead of prompt
      // MolReloadPwaModal = (await import('$lib/components/mols/MolReloadPWAModal.svelte'))
      // 	.default;
    }
  }

  // When the lang changes, we update the user lang, we also update it when the page loads, this is especially useful for older user that won't toggle their lang to et the right one on the server
  $: currentUserId = $currentUser?.id // It's important to have this, otherwise updateUserLang() would be triggered in a loop
  $: currentUserName = `${$currentUser?.firstName} ${$currentUser?.lastName}`
  $: Sentry.setUser({
    id: currentUserId || randomUUID(),
    name: currentUserName,
  })
  $: if ($locale && currentUserId && $apiStatus === 'authenticated') updateUserLang()
  const updateUserLang = async () => {
    if (currentUserId) {
      $currentUser = await useFixeeClient().service('user').patch(currentUserId, {
        lang: $locale,
      })
    }
  }

  onMount(async () => {
    // If there is no selectedLocale, we use the browser locale by default
    const selectedLocale = window.localStorage.getItem(SELECTED_LOCALE)
    if (selectedLocale) $locale = selectedLocale

    // This is important for push notification subscription, to make the device unique instead of relying on the user agent
    const uniqueDeviceId = window.localStorage.getItem(UNIQUE_DEVICE_ID)
    if (!uniqueDeviceId) window.localStorage.setItem(UNIQUE_DEVICE_ID, randomUUID())

    initFirebase()
    subscribeSwToPushNotifications()

    handleMyPermissions()

    window.addEventListener('resize', setWindowValues)
    setWindowValues()

    // Here we make sure to set the notification permission status, this is useful for browsers that don't support the permissions API like SAFARI de ses morts.
    // So we just set the value to the current Notification.permission value and it's nice
    // On iOS (and other platforms maybe) Notification is not in the window, we have to check this
    if (currentBrowserSupportsPush()) {
      $permissions.notifications = Notification.permission === 'default' ? 'prompt' : Notification.permission
    }

    // Prevent zoom on the page https://stackoverflow.com/a/39711930
    if (iOS()) {
      document.addEventListener('gesturestart', e => {
        if (inDevelopment())
          console.log(
            'Gestures have been disabled for a better experience on iOS, make it available if we need to zoom in an image, then disable it again',
          )
        e.preventDefault()
      })
    }

    useFixeeClient().service('team').on('patched', onTeamPatched)

    // Handle events authenticated/logout, connect/disconnect
    useFixeeClient().io?.on('disconnect', (reason: any) => {
      console.log('Disconnected from FIXEE server : ', reason)
      $apiStatus = 'disconnected'
      reportsStore.deinit()
    })
    useFixeeClient().io?.on('connect', async () => {
      $apiStatus = 'connected'
      checkVersion()
    })

    useFixeeClient().on('authenticated', async authResult => {
      $apiStatus = 'authenticated'
      $currentUser = authResult.user
      $teams = await loadMyTeams()
      const invitationTeams = await useFixeeClient().service('team-user').getMyInvitations()
      const invitationTeamIds = invitationTeams.map(tu => tu.teamId)
      reportsStore.init()

      const validTeams = $teams.filter(
        team => team.individualId !== $currentUser?.id && !invitationTeamIds.includes(team.id),
      )
      $isIssuerView = validTeams.filter(t => !t.individualId).length === 0

      // Set the $currentTeam to the individual team of the user if none is selected OR if we can't find the currentTeam in the teams that the user should have
      // It is useful when you change account on the same computer and "logout" wasn't properly triggered
      if (!$currentTeam || !validTeams.find(t => t.id === $currentTeam?.id)) {
        $currentTeam = validTeams[0] || undefined
      } else {
        // If currentTeam already has a value, we still "refresh" it by getting the team from the list of teams. This way we get the new amount of credits for example (instead of it being saved in the cookie and not updating well)
        $currentTeam = validTeams.find(t => t.id === $currentTeam?.id)
      }

      if (!$currentTeam) {
        // Ensure user is either on my-requests or profile page
        if (!$page.url.pathname.startsWith(routes.myRequests) && !$page.url.pathname.startsWith(routes.profile)) {
          goto(routes.myRequests)
        }
      }

      // When the user logs in, if the permission is already granted, we refresh the subscription. Maybe it's a little overkill but we're making sure the user is subscribed
      if ($permissions.notifications === 'granted') {
        requestNotificationPermission($_)
      }

      if ($currentTeam && $currentUser) {
        $currentTeamUser = (
          await useFixeeClient()
            .service('team-user')
            .find({
              query: {
                teamId: $currentTeam?.id,
                userId: $currentUser?.id,
                $limit: 1,
              },
            })
        ).data[0]
      } else {
        $currentTeam = undefined
        $currentTeamUser = undefined
      }

      checkVersion()
    })
    useFixeeClient().on('logout', async () => {
      $apiStatus = 'connected'
      $currentUser = undefined
      $currentTeam = undefined
      $currentTeamUser = undefined
      $teams = []
    })

    loadPwaModal()
  })

  onDestroy(() => {
    useFixeeClient().service('team').off('patched', onTeamPatched)
    reportsStore.deinit()

    // onDestroy can be called on the server side renderering and at this time window is undefined, it's a little weird but it's good to check for isClientSide()
    if (isClientSide()) {
      window.removeEventListener('resize', setWindowValues)
    }
  })

  const loadCurrentTeamUser = async (chatClientDefined: boolean, teamId?: string, userId?: string) => {
    if (!teamId || !userId) {
      return
    }

    if (chatClientDefined && $currentTeamUser?.teamId === teamId && $currentTeamUser?.userId === userId) {
      console.log('Current team user already loaded')
      return
    }

    const result = (
      await useFixeeClient()
        .service('team-user')
        .find({
          query: {
            teamId,
            userId,
            $limit: 1,
          },
        })
    ).data[0]

    if (teamId !== $currentTeam?.id || userId !== $currentUser?.id) {
      console.error('Current team or user has changed')
      await loadCurrentTeamUser(!!$chatClient, $currentTeam?.id, $currentUser?.id)
      return
    }

    if (!result?.conversationJwt) {
      console.error('Current team user not found or no conversationJwt')
      return
    }

    $currentTeamUser = result
    $chatClient = new ConversationsClient(result.conversationJwt, {
      logLevel: inDevelopment() || inStaging() ? 'info' : undefined,
      region: 'ie1',
    })
  }

  $: currentTeamId = $currentTeam?.id
  $: chatClientDefined = !!$chatClient
  $: if ($apiStatus === 'authenticated') {
    loadCurrentTeamUser(chatClientDefined, currentTeamId, currentUserId)
  }

  // Example here on how to handle the manifest string https://github.com/vite-pwa/sveltekit/blob/84c4c20572c7ab364df34ab205fd9fdf263a635a/examples/sveltekit-ts/src/routes/%2Blayout.svelte#L12
  $: webManifest = pwaInfo ? pwaInfo.webManifest.linkTag : ''

  // Very important, for the client of our client, we don't show the tenant logo, it's especially important when a client has customization enabled, we don't want his client to see our logo
  $: canShowTenantLogo = !$page.route?.id?.startsWith('/i/') && !$page.route?.id?.startsWith('/a/')
</script>

<svelte:head>
  <title>{$_('head.title')}</title>
  <meta name="description" content={$_('head.description')} />

  {#if inProduction()}
    <script>
      ;(function (c, s, q, u, a, r, e) {
        c.hj =
          c.hj ||
          function () {
            ;(c.hj.q = c.hj.q || []).push(arguments)
          }
        c._hjSettings = { hjid: a }
        r = s.getElementsByTagName('head')[0]
        e = s.createElement('script')
        e.async = true
        e.src = q + c._hjSettings.hjid + u
        r.appendChild(e)
      })(window, document, 'https://static.hj.contentsquare.net/c/csq-', '.js', 5255846)
    </script>
  {/if}

  {#if browser}
    {@html webManifest}
  {/if}
</svelte:head>

<MolVideoPermissionsWarning />
<MolFrontendVersionUpdate />

<!-- Using an absolute right under the body is good because we can easily use the full height of the
screen AND on mobile we don't have issues with the url bar, it's always shown which is good.
Otherwise it would be annoying to have to scroll down to see the bottom buttons bar -->
<!-- HandlerTags has to be up top because the portal uses this layout and we wan't to show tags in this portal -->
<HandlerTags currentTeam={$currentTeam}>
  <HandlerContactCompanies>
    <HandlerNotifications currentTeam={$currentTeam}>
      <HandlerUsage {currentTeam} {currentSubscription} {teams} />
      <div class="absolute top-0 bottom-0 left-0 right-0 flex flex-col">
        <slot />

        {#if browser}
          <!-- <MolReloadPwaModal /> -->
          <!-- <svelte:component this={MolReloadPwaModal} /> -->
        {/if}

        <MolComingSoonModal />
      </div>

      <!-- We don't show the page until it's loaded because there are some UI glitches, like the font is not loaded yet -->
      {#if loading && canShowTenantLogo}
        <div
          out:fade|global={{ duration: 150 }}
          class="absolute top-0 bottom-0 left-0 right-0 bg-white z-50 flex justify-center items-center"
        >
          <img class="w-28 top-1/2 left-1/2 translate-1/2" alt="logo" src="/images/logos/fixee_logo_transparent.png" />
        </div>
      {/if}
    </HandlerNotifications>
  </HandlerContactCompanies>
</HandlerTags>

<!-- https://github.com/beyonk-group/svelte-google-analytics#the-enabled-property-optional Handle RGPD by calling init() later -->
{#if inProduction()}
  <GoogleAnalytics properties={[PUBLIC_GOOGLE_ANALYTICS_ID]} />
{/if}

<Toaster position={'top-right'} toastOptions={{ duration: 6000 }} />
